import {
  Disclaimer,
  Div,
  P,
  Pill,
  UndoIcon,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { MenuAiItemChange } from '../../../types/api/menuAi.types';
import { hasItemChange } from './menuAiChanges.helpers';
import { ItemGlare, RefineIconButton } from './menuAiGlow.styled';
import { deltaColor, signedCents, signedPct } from './priceChangeFormat';

/** DOM id of a change box — used to scroll it into view from the preview. */
export const aiChangeDomId = (itemId: number) => `ai-change-${itemId}`;

/** Small outline chip naming one kind of change on an item. */
const Badge = ({ label }: { label: string }) => (
  <Pill
    $minWidth="0"
    $padding="2px 10px"
    $background={lightTheme.aiGradient}
    $border={lightTheme.white}
  >
    <Disclaimer $m="0" $color={lightTheme.white}>
      {label}
    </Disclaimer>
  </Pill>
);

/**
 * Heads one change section with its own pill — so within a single item the
 * name / price / category / … changes read as separate labelled blocks. The
 * flex wrapper keeps the pill at its content width (left-aligned).
 */
const SectionBadge = ({ label }: { label: string }) => (
  <Div $flex $mt="8" $mb="4">
    <Badge label={label} />
  </Div>
);

/**
 * "Changes" tab of the AI card: everything the AI touched, item by item —
 * change-type badges, the old → new details and the AI's reasons, its changed
 * modifier options, and a Revert button (top-right) that reverts the whole item
 * to the original menu. Item price rows carry no delta/pct from the backend
 * (unlike modifier rows), so those are computed here.
 */
export const MenuAIChangesPanel = ({
  changes,
  onItemClick,
  onRevert,
  highlightItemId,
}: {
  changes: MenuAiItemChange[];
  // Focus the item in the left preview (scroll + glare) when its box is clicked.
  onItemClick?: (itemId: number) => void;
  // Revert the whole item back to the original menu.
  onRevert?: (itemId: number) => void;
  // Item whose change box should play a one-shot glare (set from a preview click).
  highlightItemId?: number | null;
}) => {
  const changedItems = changes.filter(hasItemChange);

  if (changedItems.length === 0) {
    return (
      <P $m="0" $center $color={lightTheme.lightGray}>
        No changes recorded.
      </P>
    );
  }

  return (
    <div>
      {changedItems.map((c, i) => {
        const delta = (c.newPriceCents ?? 0) - (c.oldPriceCents ?? 0);
        const pct = c.oldPriceCents
          ? Math.round((delta / c.oldPriceCents) * 100)
          : 0;
        return (
          <Div
            key={`${c.itemId}-${i}`}
            id={aiChangeDomId(c.itemId)}
            $box
            $p="12px 20px"
            $pSM="12px 16px"
            $mb="8"
            $pointer={!!onItemClick}
            onClick={onItemClick ? () => onItemClick(c.itemId) : undefined}
            style={{ position: 'relative' }}
          >
            {highlightItemId === c.itemId && <ItemGlare />}
            <Div $flex $between $alignCenter $gap="8px 12px">
              <P $m="0" $bold $left style={{ flex: 1, minWidth: '80px' }}>
                {c.name}
              </P>
              <Div $flex $alignCenter $gap="8px" $flexNone>
                {onRevert && (
                  <RefineIconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onRevert(c.itemId);
                    }}
                    title="Revert this item"
                  >
                    <UndoIcon color={lightTheme.darkGray} />
                  </RefineIconButton>
                )}
              </Div>
            </Div>

            {c.nameChanged && (
              <>
                <SectionBadge label="Name" />
                {c.oldName && (
                  <P
                    $m="0"
                    $left
                    $color={lightTheme.lightGray}
                    style={{ textDecoration: 'line-through' }}
                  >
                    {c.oldName}
                  </P>
                )}
                {c.nameReason && (
                  <Disclaimer $m="0" $left $color={lightTheme.lightGray}>
                    <b>AI Response:</b> {c.nameReason}
                  </Disclaimer>
                )}
              </>
            )}

            {c.priceChanged && (
              <>
                <SectionBadge label="Price" />
                <Div $flex $alignCenter $gap="8px">
                  <P
                    $m="0"
                    $color={lightTheme.lightGray}
                    style={{ textDecoration: 'line-through' }}
                  >
                    {displayCentsInDollars(c.oldPriceCents ?? 0)}
                  </P>
                  <P $m="0" $bold>
                    {displayCentsInDollars(c.newPriceCents ?? 0)}
                  </P>
                  <Disclaimer $m="0" $color={deltaColor(delta)}>
                    {signedCents(delta)} ({signedPct(pct)})
                  </Disclaimer>
                </Div>
                {c.priceReason && (
                  <Disclaimer $m="0" $left $color={lightTheme.lightGray}>
                    <b>AI Response:</b> {c.priceReason}
                  </Disclaimer>
                )}
              </>
            )}

            {(c.movedCategory || c.categoryRenamed) && (
              <>
                <SectionBadge
                  label={c.categoryRenamed ? 'Category renamed' : 'Moved'}
                />
                <P $m="0" $left>
                  <span style={{ color: lightTheme.lightGray }}>
                    {c.oldCategory}
                  </span>
                  {' → '}
                  {c.newCategory}
                </P>
                {c.categoryReason && (
                  <Disclaimer $m="0" $left $color={lightTheme.lightGray}>
                    <b>AI Response:</b> {c.categoryReason}
                  </Disclaimer>
                )}
              </>
            )}

            {c.orderChanged && (
              <>
                <SectionBadge label="Reordered" />
                <P $m="0" $left>
                  <span style={{ color: lightTheme.lightGray }}>
                    Position #{c.oldOrder ?? '—'}
                  </span>
                  {' → '}#{c.newOrder ?? '—'}
                </P>
              </>
            )}

            {c.descriptionChanged && (
              <>
                <SectionBadge label="Description" />
                {(c.oldDescription || c.newDescription) && (
                  <div>
                    {c.oldDescription && (
                      <P
                        $m="0"
                        $left
                        $color={lightTheme.lightGray}
                        style={{ textDecoration: 'line-through' }}
                      >
                        {c.oldDescription}
                      </P>
                    )}
                    {c.newDescription && (
                      <P $m="0" $left>
                        {c.newDescription}
                      </P>
                    )}
                  </div>
                )}
                {c.descriptionReason && (
                  <Disclaimer
                    $m="0"
                    $mt="8"
                    $left
                    $color={lightTheme.lightGray}
                  >
                    <b>AI Response:</b> {c.descriptionReason}
                  </Disclaimer>
                )}
              </>
            )}

            {c.prepNotesChanged && (
              <>
                {/* Empty old value → the AI added prep notes (item had none). */}
                <SectionBadge
                  label={c.oldPrepNotes ? 'Prep notes' : 'Prep notes added'}
                />
                {(c.oldPrepNotes || c.newPrepNotes) && (
                  <div>
                    {c.oldPrepNotes && (
                      <P
                        $m="0"
                        $left
                        $color={lightTheme.lightGray}
                        style={{ textDecoration: 'line-through' }}
                      >
                        {c.oldPrepNotes}
                      </P>
                    )}
                    {c.newPrepNotes && (
                      <P $m="0" $left>
                        {c.newPrepNotes}
                      </P>
                    )}
                  </div>
                )}
                {c.prepNotesReason && (
                  <Disclaimer
                    $m="0"
                    $mt="8"
                    $left
                    $color={lightTheme.lightGray}
                  >
                    <b>AI Response:</b> {c.prepNotesReason}
                  </Disclaimer>
                )}
              </>
            )}

            {c.imageChanged && (
              <>
                <SectionBadge label="Image" />
                <Disclaimer $m="0" $left $color={lightTheme.lightGray}>
                  A new image was generated for this item.
                </Disclaimer>
              </>
            )}

            {c.modifiers && c.modifiers.length > 0 && (
              <>
                <SectionBadge label="Modifiers" />
                {c.modifiers.map((m, mi) => (
                  <Div
                    key={`${m.groupId}-${m.optionName}-${mi}`}
                    $mt={mi === 0 ? '0' : '12'}
                  >
                    <P $m="0" $left>
                      {m.groupName} • {m.optionName}
                    </P>
                    <Div $flex $alignCenter $gap="8px">
                      <P
                        $m="0"
                        $color={lightTheme.lightGray}
                        style={{ textDecoration: 'line-through' }}
                      >
                        {displayCentsInDollars(m.oldPriceCents)}
                      </P>
                      <P $m="0" $bold>
                        {displayCentsInDollars(m.newPriceCents)}
                      </P>
                      <Disclaimer $m="0" $color={deltaColor(m.deltaCents)}>
                        {signedCents(m.deltaCents)} ({signedPct(m.pct)})
                      </Disclaimer>
                    </Div>
                    {m.reason && (
                      <Disclaimer $m="0" $left $color={lightTheme.lightGray}>
                        <b>AI Response:</b> {m.reason}
                      </Disclaimer>
                    )}
                  </Div>
                ))}
              </>
            )}
          </Div>
        );
      })}
    </div>
  );
};
